import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, formatDate, formatDateTime, newIdempotencyKey } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { hasAnyRole, M16_ROLES } from '../../lib/roles';
import type { ChecklistItem, FindingItem, WorkplaceInspection } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, InfoNote, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function InspectionDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { persona } = useAuth();
  const iq = useApiQuery<WorkplaceInspection>(`/api/v1/m16/inspections/${id}`);
  const isChief = hasAnyRole(persona?.roles, M16_ROLES.CHIEF_INSPECTOR);
  const isInspectionStaff = hasAnyRole(persona?.roles, M16_ROLES.LABOUR_INSPECTOR, M16_ROLES.CHIEF_INSPECTOR);

  if (iq.loading) return <Spinner label="Loading inspection…" />;
  if (iq.error) return <ErrorNote message={iq.error} />;
  if (!iq.data) return <ErrorNote message="Not found" />;
  const insp = iq.data;

  return (
    <div>
      <PageHeader
        title={insp.refNo}
        subtitle={`${insp.type} inspection — ${insp.employerEntityId}${insp.district ? ` · ${insp.district}` : ''}`}
        actions={<StatusBadge status={insp.status} />}
      />

      <div className="mb-6 grid gap-5 lg:grid-cols-3">
        <div className="card p-5 lg:col-span-2">
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-xs font-semibold uppercase text-slate-400">Scheduled</dt>
              <dd className="text-slate-800">{insp.scheduledAt ? formatDateTime(insp.scheduledAt) : '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase text-slate-400">Conducted</dt>
              <dd className="text-slate-800">{insp.conductedAt ? formatDateTime(insp.conductedAt) : '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase text-slate-400">Inspector</dt>
              <dd className="font-mono text-slate-800">{insp.inspectorId ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase text-slate-400">Follow-up of</dt>
              <dd className="text-slate-800">
                {insp.followUpOfId ? (
                  <Link className="font-mono text-brand-600 hover:underline" to={`/inspections/${insp.followUpOfId}`}>
                    {insp.followUpOfId}
                  </Link>
                ) : (
                  '—'
                )}
              </dd>
            </div>
            {insp.relatedGrievanceId ? (
              <div className="col-span-2">
                <dt className="text-xs font-semibold uppercase text-slate-400">Related grievance</dt>
                <dd>
                  <Link className="font-mono text-brand-600 hover:underline" to={`/grievances/${insp.relatedGrievanceId}`}>
                    {insp.relatedGrievanceId}
                  </Link>
                </dd>
              </div>
            ) : null}
          </dl>

          {insp.checklist && insp.checklist.length > 0 ? (
            <div className="mt-4 border-t border-slate-100 pt-3">
              <div className="mb-1 text-xs font-semibold uppercase text-slate-400">Checklist</div>
              <ul className="space-y-1">
                {insp.checklist.map((c, i) => (
                  <li key={i} className="flex items-center justify-between text-sm">
                    <span>
                      {c.label} <span className="text-xs text-slate-400">({c.code})</span>
                      {c.note ? <div className="text-xs text-slate-500">{c.note}</div> : null}
                    </span>
                    <StatusBadge status={c.result} />
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {insp.findings && insp.findings.length > 0 ? (
            <div className="mt-4 border-t border-slate-100 pt-3">
              <div className="mb-1 text-xs font-semibold uppercase text-slate-400">Findings</div>
              <ul className="space-y-1.5">
                {insp.findings.map((f, i) => (
                  <li key={i} className="text-sm">
                    <StatusBadge status={f.severity} /> <span className="ml-1">{f.description}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {insp.complianceStatus ? (
            <div className="mt-4 border-t border-slate-100 pt-3">
              <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase text-slate-400">
                Compliance notice <StatusBadge status={insp.complianceStatus} />
              </div>
              <ul className="mb-1 list-inside list-disc text-sm text-slate-700">
                {insp.complianceItems.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
              <div className="text-xs text-slate-500">Deadline {formatDate(insp.complianceDeadline)}</div>
              {insp.complianceCheckedAt ? (
                <div className="text-xs text-slate-500">
                  Checked {formatDateTime(insp.complianceCheckedAt)}
                  {insp.complianceNotes ? ` — ${insp.complianceNotes}` : ''}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="space-y-4">
          {isChief && insp.status !== 'COMPLETED' && insp.status !== 'CANCELLED' ? <AssignCard inspectionId={insp.id} onDone={iq.reload} /> : null}

          {isInspectionStaff && (insp.status === 'SCHEDULED' || insp.status === 'IN_PROGRESS') ? (
            <ChecklistCard inspectionId={insp.id} onSubmitted={iq.reload} />
          ) : null}

          {isInspectionStaff && insp.status === 'COMPLETED' && insp.complianceStatus === null ? (
            <ComplianceNoticeCard inspectionId={insp.id} onDone={iq.reload} />
          ) : null}

          {isInspectionStaff && insp.complianceStatus === 'OPEN' ? <ComplianceCheckCard inspectionId={insp.id} onDone={iq.reload} /> : null}

          {isInspectionStaff && insp.complianceStatus === 'OVERDUE' ? (
            <FollowUpCard inspectionId={insp.id} onScheduled={(followUpId) => navigate(`/inspections/${followUpId}`)} />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function AssignCard({ inspectionId, onDone }: { inspectionId: string; onDone: () => void }) {
  const [inspectorId, setInspectorId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const assign = async () => {
    if (!inspectorId.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m16/inspections/${inspectionId}/assign`, { inspectorId: inspectorId.trim() });
      setInspectorId('');
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Assignment failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-2 text-sm font-bold text-slate-700">Assign inspector</h2>
      {error ? (
        <div className="mb-2">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <input className="field-input mb-2" value={inspectorId} onChange={(e) => setInspectorId(e.target.value)} placeholder="Inspector ID" />
      <button className="btn-primary w-full" onClick={assign} disabled={busy || !inspectorId.trim()}>
        {busy ? 'Assigning…' : 'Assign'}
      </button>
    </div>
  );
}

const CHECKLIST_RESULTS = ['PASS', 'FAIL', 'NA'] as const;
const SEVERITIES = ['LOW', 'MEDIUM', 'HIGH'] as const;

function ChecklistCard({ inspectionId, onSubmitted }: { inspectionId: string; onSubmitted: () => void }) {
  const [checklist, setChecklist] = useState<ChecklistItem[]>([{ code: '', label: '', result: 'PASS', note: '' }]);
  const [findings, setFindings] = useState<FindingItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validChecklist = checklist.filter((c) => c.code.trim() && c.label.trim());

  const submit = async () => {
    if (validChecklist.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(
        `/api/v1/m16/inspections/${inspectionId}/checklist`,
        {
          checklist: validChecklist.map((c) => ({ code: c.code.trim(), label: c.label.trim(), result: c.result, note: c.note?.trim() || undefined })),
          findings: findings
            .filter((f) => f.code.trim() && f.description.trim())
            .map((f) => ({ code: f.code.trim(), severity: f.severity, description: f.description.trim() })),
        },
        { idempotencyKey: newIdempotencyKey() },
      );
      onSubmitted();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Checklist submission failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Submit checklist</h2>
      <p className="mb-2 text-xs text-slate-500">Offline-filled, synced in one shot — safe to retry (Idempotency-Key).</p>
      {error ? (
        <div className="mb-2">
          <ErrorNote message={error} />
        </div>
      ) : null}

      <div className="mb-2 space-y-2">
        {checklist.map((c, i) => (
          <div key={i} className="rounded-lg border border-slate-200 p-2">
            <div className="grid grid-cols-2 gap-1.5">
              <input className="field-input !py-1 text-xs" placeholder="Code" value={c.code} onChange={(e) => setChecklist((rows) => rows.map((r, j) => (j === i ? { ...r, code: e.target.value } : r)))} />
              <select className="field-input !py-1 text-xs" value={c.result} onChange={(e) => setChecklist((rows) => rows.map((r, j) => (j === i ? { ...r, result: e.target.value as ChecklistItem['result'] } : r)))}>
                {CHECKLIST_RESULTS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <input
              className="field-input !py-1 mt-1.5 text-xs"
              placeholder="Label"
              value={c.label}
              onChange={(e) => setChecklist((rows) => rows.map((r, j) => (j === i ? { ...r, label: e.target.value } : r)))}
            />
            <input
              className="field-input !py-1 mt-1.5 text-xs"
              placeholder="Note (optional)"
              value={c.note}
              onChange={(e) => setChecklist((rows) => rows.map((r, j) => (j === i ? { ...r, note: e.target.value } : r)))}
            />
            {checklist.length > 1 ? (
              <button className="mt-1 text-[11px] font-semibold text-rose-600" onClick={() => setChecklist((rows) => rows.filter((_, j) => j !== i))}>
                Remove
              </button>
            ) : null}
          </div>
        ))}
        <button
          className="btn-secondary w-full !py-1 text-xs"
          onClick={() => setChecklist((rows) => [...rows, { code: '', label: '', result: 'PASS', note: '' }])}
        >
          + Add checklist item
        </button>
      </div>

      <div className="mb-3 space-y-2">
        <div className="text-xs font-bold uppercase text-slate-500">Findings (optional)</div>
        {findings.map((f, i) => (
          <div key={i} className="rounded-lg border border-slate-200 p-2">
            <div className="grid grid-cols-2 gap-1.5">
              <input className="field-input !py-1 text-xs" placeholder="Code" value={f.code} onChange={(e) => setFindings((rows) => rows.map((r, j) => (j === i ? { ...r, code: e.target.value } : r)))} />
              <select className="field-input !py-1 text-xs" value={f.severity} onChange={(e) => setFindings((rows) => rows.map((r, j) => (j === i ? { ...r, severity: e.target.value as FindingItem['severity'] } : r)))}>
                {SEVERITIES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <input
              className="field-input !py-1 mt-1.5 text-xs"
              placeholder="Description"
              value={f.description}
              onChange={(e) => setFindings((rows) => rows.map((r, j) => (j === i ? { ...r, description: e.target.value } : r)))}
            />
            <button className="mt-1 text-[11px] font-semibold text-rose-600" onClick={() => setFindings((rows) => rows.filter((_, j) => j !== i))}>
              Remove
            </button>
          </div>
        ))}
        <button className="btn-secondary w-full !py-1 text-xs" onClick={() => setFindings((rows) => [...rows, { code: '', severity: 'LOW', description: '' }])}>
          + Add finding
        </button>
      </div>

      <button className="btn-primary w-full" onClick={submit} disabled={busy || validChecklist.length === 0}>
        {busy ? 'Submitting…' : 'Submit checklist (completes inspection)'}
      </button>
    </div>
  );
}

function ComplianceNoticeCard({ inspectionId, onDone }: { inspectionId: string; onDone: () => void }) {
  const [itemsText, setItemsText] = useState('');
  const [deadlineDays, setDeadlineDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const items = itemsText
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);

  const submit = async () => {
    if (items.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m16/inspections/${inspectionId}/compliance-notice`, { items, deadlineDays });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not issue notice');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-2 text-sm font-bold text-slate-700">Issue compliance notice</h2>
      {error ? (
        <div className="mb-2">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <Field label="Items — one per line" className="mb-2">
        <textarea className="field-input min-h-[5rem]" value={itemsText} onChange={(e) => setItemsText(e.target.value)} placeholder={'Revise kitchen staff rota…\nInstall additional fire extinguisher…'} />
      </Field>
      <Field label="Deadline (days)" className="mb-2">
        <input type="number" min={1} max={365} className="field-input" value={deadlineDays} onChange={(e) => setDeadlineDays(Number(e.target.value))} />
      </Field>
      <button className="btn-primary w-full" onClick={submit} disabled={busy || items.length === 0}>
        {busy ? 'Issuing…' : 'Issue notice'}
      </button>
    </div>
  );
}

function ComplianceCheckCard({ inspectionId, onDone }: { inspectionId: string; onDone: () => void }) {
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState<'MET' | 'OVERDUE' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const record = async (compliant: boolean) => {
    setBusy(compliant ? 'MET' : 'OVERDUE');
    setError(null);
    try {
      await api.post(`/api/v1/m16/inspections/${inspectionId}/compliance-check`, { compliant, notes: notes.trim() || undefined });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not record check');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-2 text-sm font-bold text-slate-700">Record compliance check</h2>
      {error ? (
        <div className="mb-2">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <textarea className="field-input mb-2 min-h-[3rem]" placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
      <div className="flex gap-2">
        <button className="btn-success flex-1" onClick={() => record(true)} disabled={busy !== null}>
          {busy === 'MET' ? 'Saving…' : 'Compliant (MET)'}
        </button>
        <button className="btn-danger flex-1" onClick={() => record(false)} disabled={busy !== null}>
          {busy === 'OVERDUE' ? 'Saving…' : 'Non-compliant (OVERDUE)'}
        </button>
      </div>
    </div>
  );
}

function FollowUpCard({ inspectionId, onScheduled }: { inspectionId: string; onScheduled: (followUpId: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const schedule = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<WorkplaceInspection>(`/api/v1/m16/inspections/${inspectionId}/follow-up`);
      onScheduled(res.id);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not schedule follow-up');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-2 text-sm font-bold text-slate-700">Follow-up inspection</h2>
      <InfoNote message="The last compliance check found non-compliance — schedule a follow-up." />
      {error ? (
        <div className="mt-2">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <button className="btn-primary mt-3 w-full" onClick={schedule} disabled={busy}>
        {busy ? 'Scheduling…' : 'Schedule follow-up'}
      </button>
    </div>
  );
}
